/**
 * Hotel Intent Handler
 *
 * Handles intents from hotel QnA responses to trigger appropriate UI actions.
 * This is extensible - add new intent handlers as needed.
 *
 * @example
 * ```typescript
 * // In message thunk after receiving response
 * const intents = extractIntentsFromResponse(templateData);
 * if (intents.length > 0) {
 *   handleHotelIntents(intents, templateData, dispatch);
 * }
 * ```
 */
import type { AppDispatch } from '@/store';
import { openReviews } from '../redux/slices/detailsSlice';
import { showQnAGalleryPopup, type QnAMediaCategory } from '../redux/slices/popupsSlice';
import { logger } from '@/shared/utils/logger';

// ============================================
// Intent Types
// ============================================

/**
 * Known hotel intents that trigger UI actions
 */
export const HOTEL_INTENTS = {
  /** Show hotel media/images - opens gallery popup */
  HOTEL_MEDIA: 'HOTEL_MEDIA',
  /** Show hotel reviews - opens reviews popup */
  HOTEL_REVIEWS: 'HOTEL_REVIEWS',
  /** Show hotel details - default detail view */
  HOTEL_DETAIL: 'HOTEL_DETAIL',
  /** Compare hotels */
  HOTEL_COMPARE: 'HOTEL_COMPARE',
} as const;

export type HotelIntent = (typeof HOTEL_INTENTS)[keyof typeof HOTEL_INTENTS];

// ============================================
// Intent Extraction
// ============================================

/**
 * Extract intents from hotel QnA template data
 *
 * Looks for intents in:
 * - searchContext.intents (from requestParameter)
 * - metaContext.requestParameter.intents
 *
 * @param templateData - The template data from the response
 * @returns Array of intent strings
 */
export function extractIntentsFromHotelResponse(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  templateData: Record<string, any> | null | undefined,
): string[] {
  if (!templateData) {
    return [];
  }

  // Try searchContext.intents (common location after CB processing)
  const searchContext = templateData['searchContext'] as Record<string, unknown> | undefined;
  if (searchContext?.['intents'] && Array.isArray(searchContext['intents'])) {
    return searchContext['intents'] as string[];
  }

  // Try metaContext.requestParameter.intents (camelCase from agent, snake_case from CB serialization)
  const metaContext = (templateData['metaContext'] ?? templateData['meta_context']) as
    | Record<string, unknown>
    | undefined;
  if (metaContext) {
    const requestParameter = metaContext['requestParameter'] as Record<string, unknown> | undefined;
    if (requestParameter?.['intents'] && Array.isArray(requestParameter['intents'])) {
      return requestParameter['intents'] as string[];
    }
  }

  // Try hotels node (for QnA responses — CB serializes key as meta_context)
  const hotels = templateData['hotels'] as Record<string, unknown> | undefined;
  if (hotels) {
    const hotelsMetaContext = (hotels['metaContext'] ?? hotels['meta_context']) as
      | Record<string, unknown>
      | undefined;
    if (hotelsMetaContext) {
      const hotelsRequestParam = hotelsMetaContext['requestParameter'] as
        | Record<string, unknown>
        | undefined;
      if (hotelsRequestParam?.['intents'] && Array.isArray(hotelsRequestParam['intents'])) {
        return hotelsRequestParam['intents'] as string[];
      }
    }
  }

  return [];
}

// ============================================
// Intent Handlers
// ============================================

/**
 * Intent handler function type
 */
type IntentHandler = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  templateData: Record<string, any>,
  dispatch: AppDispatch,
) => void;

/**
 * Extract media categories from QnA template data.
 *
 * Format: hotels.resources[].data.agentResponse.media.{hotel, traveller}
 *   - media.hotel[]   → "Property" tab categories
 *   - media.traveller[] → "Traveller" tab categories
 *
 * Exported so MessageCard can reuse the same logic for gallery reopening.
 */
export function extractMediaFromQnATemplateData(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  templateData: Record<string, any>,
): QnAMediaCategory[] {
  try {
    const hotels = templateData['hotels'] as Record<string, unknown> | undefined;
    if (!hotels) return [];

    const resources = hotels['resources'] as Array<Record<string, unknown>> | undefined;
    if (!resources || resources.length === 0) return [];

    for (const resource of resources) {
      const data = resource['data'] as Record<string, unknown> | undefined;
      if (!data) continue;

      const agentResponse = data['agentResponse'] as Record<string, unknown> | undefined;
      if (!agentResponse) continue;

      const media = agentResponse['media'] as Record<string, unknown> | undefined;
      if (!media) continue;

      const result: QnAMediaCategory[] = [];

      // New format: media.hotel + media.traveller
      const hotelCategories = media['hotel'] as Array<Record<string, unknown>> | undefined;
      const travellerCategories = media['traveller'] as Array<Record<string, unknown>> | undefined;

      if (hotelCategories || travellerCategories) {
        for (const category of hotelCategories ?? []) {
          result.push({
            tab: 'Property',
            category: (category['category'] as string) || 'Images',
            images: (category['images'] as string[]) || [],
            image_count: (category['image_count'] as number) || 0,
          });
        }
        for (const category of travellerCategories ?? []) {
          result.push({
            tab: 'Traveller',
            category: (category['category'] as string) || 'Images',
            images: (category['images'] as string[]) || [],
            image_count: (category['image_count'] as number) || 0,
          });
        }
        if (result.length > 0) return result;
      }

      // Legacy format: media.categories with "(Property)" / "(Traveller)" in name
      const legacyCategories = media['categories'] as Array<Record<string, unknown>> | undefined;
      if (legacyCategories && legacyCategories.length > 0) {
        return legacyCategories.map((category) => {
          const rawName = (category['category'] as string) || 'Images';
          const match = rawName.match(/^(.+?)\s*\((\w+)\)$/);
          return {
            tab: match?.[2]?.trim() ?? 'Property',
            category: match?.[1]?.trim() ?? rawName,
            images: (category['images'] as string[]) || [],
            image_count: (category['image_count'] as number) || 0,
          };
        });
      }
    }

    return [];
  } catch (e) {
    logger.error('INTENT_HANDLER', 'Failed to extract media from QnA response', e);
    return [];
  }
}

/**
 * Registry of intent handlers
 * Add new handlers here for new intents
 */
const intentHandlers: Record<string, IntentHandler> = {
  /**
   * HOTEL_MEDIA intent - opens the gallery popup with QnA media
   */
  [HOTEL_INTENTS.HOTEL_MEDIA]: (templateData, dispatch) => {
    const mediaCategories = extractMediaFromQnATemplateData(templateData);

    const categoriesWithImages = mediaCategories.filter(
      (category) => category.images && category.images.length > 0,
    );

    if (categoriesWithImages.length > 0) {
      dispatch(showQnAGalleryPopup(categoriesWithImages));
    }
  },

  /**
   * HOTEL_REVIEWS intent - opens the reviews popup
   */
  [HOTEL_INTENTS.HOTEL_REVIEWS]: (_templateData, dispatch) => {
    dispatch(openReviews());
  },

  // Add more intent handlers here as needed
  // [HOTEL_INTENTS.HOTEL_COMPARE]: (templateData, dispatch) => { ... },
};

// ============================================
// Main Handler
// ============================================

/**
 * Handle hotel intents from QnA response
 *
 * Processes the intents array and triggers appropriate UI actions.
 * Only the first matching intent with a handler is processed.
 *
 * @param intents - Array of intent strings from the response
 * @param templateData - The template data from the response
 * @param dispatch - Redux dispatch function
 * @returns true if an intent was handled, false otherwise
 */
export function handleHotelIntents(
  intents: string[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  templateData: Record<string, any>,
  dispatch: AppDispatch,
): boolean {
  if (!intents?.length) {
    return false;
  }

  for (const intent of intents) {
    const handler = intentHandlers[intent];
    if (handler) {
      handler(templateData, dispatch);
      return true;
    }
  }

  return false;
}

/**
 * Check if any of the intents should trigger a popup action
 *
 * @param intents - Array of intent strings
 * @returns true if intents contain a popup-triggering intent
 */
export function hasPopupIntent(intents: string[]): boolean {
  const popupIntents: string[] = [HOTEL_INTENTS.HOTEL_MEDIA, HOTEL_INTENTS.HOTEL_REVIEWS];
  return intents.some((intent) => popupIntents.includes(intent));
}
